// @flow

import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { mediaMax } from 'components/styles/_media'
import Colors from 'components/styles/colors'
import {
  imagePath,
  ownerEvalutionImage,
} from 'frontend_core/dist/helpers/image'
import { imageURLWithTransform } from 'frontend_core/dist/helpers/format'

const HostSummary = styled.div`
  background-color: ${Colors.grayF};
`

const SubTitle = styled.h2`
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 16px;
  line-height: 1.4;
`

const Inner = styled.div`
  margin-bottom: 20px;
`

const HostContainer = styled.div`
  width: 125px;
  float: left;
  margin-bottom: 8px;
  ${mediaMax.desktop`
    width: 50%;
    float: none;
    margin: 0 auto;
  `};
`

const HostIconInner = styled.div`
  position: relative;
`

const HostIconImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  margin-top: 8px;
  margin-bottom: 12px;
  ${mediaMax.desktop`
    margin-bottom: 8px;
  `} &:hover {
    opacity: 0.8;
  }
`

const TopHostImage = styled.img`
  position: absolute;
  bottom: 5px;
  right: -4px;
  width: 38px;
  height: 50px;
  ${mediaMax.desktop`
    width: 50px;
    height: 66px;
  `};
`

const RegisteredDateLabel = styled.p`
  color: #999;
  text-align: center;
  font-size: 11px;
  line-height: 1.3;
  ${mediaMax.desktop`
    font-size: 12px;
  `};
`

const RegisteredDateText = styled.span`
  margin-left: 4px;
`

const HostDetail = styled.div`
  float: left;
  margin-left: 24px;
  ${mediaMax.desktop`
    float: none;
    margin-left: 0;
  `};
`

const HostName = styled.h3`
  font-size: 16px;
  font-weight: bold;
  margin-top: 10px;
  line-height: 1.2;
  ${mediaMax.desktop`
    text-align: center;
  `};
`

const HostLink = styled.a`
  &:link {
    color: ${props => props.linkColor};
  }
  &:hover {
    text-decoration: underline;
  }
`

const HostEvaluate = styled.div`
  margin-bottom: 16px;
  ${mediaMax.desktop`
    clear: both;
    font-size: 12px;
  `} $:after {
    content: '';
    clear: both;
    display: block;
  }
`

const EvaluateItem = styled.div`
  width: 115px;
  float: left;
  text-align: center;
  margin-top: 12px;
  &:nth-child(2) {
    width: 160px;
  }
  ${mediaMax.desktop`
    width: 33%;
    &:nth-child(2){
     width: 33%;
    }
  `};
`

const EvaluateTitle = styled.h4`
  color: #999;
  font-weight: bold;
  ${mediaMax.desktop`
    font-size: 11px;
    letter-spacing: -1px;
  `};
`

const EvaluateValue = styled.div`
  font-size: 12px;
`

const EvaluateImage = styled.img`
  width: 35px;
  height: 32px;
  margin: 14px 0;
`

const EvaluateText = styled.span`
  display: block;
`

const RowLine = styled.div`
  margin-left: -12px;
  margin-right: -12px;
  &:before,
  &:after {
    content: '';
    display: table;
  }
`

const ProfileText = styled.p`
  clear: both;
  max-height: 40px;
  overflow: hidden;
  a:hover {
    text-decoration: underline;
  }
  font-size: 12px;
  line-height: 1.6;
`

const AuthContainer = styled.div`
  clear: both;
  border: 1px solid ${Colors.grayD};
  background-color: ${Colors.white};
  border-radius: 3px;
  padding: 16px 0;
  margin-top: 8px;
  &:after {
    content: '';
    clear: both;
    display: block;
  }
  ${mediaMax.desktop`
    margin-bottom: 8px;
    img{
      width: 90%;
      height: auto;
    }
  `};
`

const AuthIcon = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
`

const AuthHeadLine = styled.h4`
  color: #999;
  margin-bottom: 12px;
  padding-top: 8px;
  line-height: 1.3;
  margin-left: -(24 / 2) px;
  ${mediaMax.phone`
   padding-top: 0;
   margin-bottom: 0px;
  `};
`

const AuthCheckList = styled.ul`
  margin-left: -(24 / 2) px;
  li {
    display: inline;
    margin-right: 16px;
  }
  .fa {
    margin-right: 4px;
    color: ${props => Colors.secondaryColors[props.service]};
  }
  ${mediaMax.phone`
    font-size: 0;
    li {
      font-size: 13px;
      display: inline-block;
      margin-top: 8px;
    }
  `};
`

type PropTypes = {
  name: string,
  profile: string,
  ownerPath: string,
  ownerProfileImage: string,
  isTopHost: boolean,
  createdAt: Date,
  confirmRate: {
    evaluation: string,
    text: string,
  },
  replyRate: {
    evaluation: string,
    text: string,
  },
  replyTimeAvg: {
    evaluation: string,
    text: string,
  },
  isVerifiedFacebook: boolean,
  isVerifiedEmail: boolean,
  isVerifiedTel: boolean,
  service: string,
}

const evaluate = (
  title: string,
  rate: { evaluation: string, text: string },
  help: ?string,
) => (
  <EvaluateItem>
    <EvaluateTitle>
      {title}
      {help ? (
        <span className="mod-evaluate__help">
          <i className="fa fa-question-circle mod-evaluate__question" />
          <span className="mod-tooltip mod-evaluate__help-tooltip">
            <span className="baloon baloon-top user-detail-baloon mod-tooltip__detail mod-evaluate__help-baloon">
              <span className="baloon-inner">{help}</span>
            </span>
          </span>
        </span>
      ) : null}
    </EvaluateTitle>
    <EvaluateValue>
      <EvaluateImage src={ownerEvalutionImage(rate.evaluation)} alt="" />
      <EvaluateText className="mod-evaluate__value-text">
        {rate.text}
      </EvaluateText>
    </EvaluateValue>
  </EvaluateItem>
)

export default (props: PropTypes) => {
  const {
    name,
    profile,
    ownerPath,
    ownerProfileImage,
    isTopHost,
    createdAt,
    confirmRate,
    replyRate,
    replyTimeAvg,
    isVerifiedFacebook,
    isVerifiedEmail,
    isVerifiedTel,
    service,
  } = props

  return (
    <HostSummary className="col-sm-12 col-md-8">
      <SubTitle>{i18n.t('page.room.ownerTitle')}</SubTitle>
      <Inner>
        <HostContainer>
          <HostIconInner>
            <a href={ownerPath}>
              <HostIconImage
                src={imageURLWithTransform(ownerProfileImage, {
                  width: 500,
                  height: 500,
                  crop: 'fill',
                })}
                alt={name}
              />
            </a>
            {isTopHost && (
              <TopHostImage
                src={imagePath('common/gold_superhost.png')}
                alt={i18n.t('page.room.tophost')}
              />
            )}
          </HostIconInner>
          <RegisteredDateLabel>
            {i18n.t('page.room.registeredDate')}
            <RegisteredDateText>
              {moment(createdAt).format(i18n.t('page.shared.dateFormat1'))}
            </RegisteredDateText>
          </RegisteredDateLabel>
          {isTopHost && (
            <div className="mod-host__superhost-area">
              <div className="mod-host__superhost-text">
                <img
                  src={imagePath('common/gold_superhost.png')}
                  alt={i18n.t('page.room.tophost')}
                />
                {i18n.t('page.room.tophost')}
                <i className="fa fa-question-circle" />
                <span className="mod-tooltip mod-host__superhost__help-tooltip">
                  <span className="baloon baloon-top user-detail-baloon mod-tooltip__detail mod-host__superhost__help-baloon">
                    <span className="baloon-inner">
                      {i18n.t('page.room.captionTophost')}
                      <span className="mod-host__superhost-notes">
                        <a
                          href="https://help.spacemarket.com/hc/ja/articles/227088227"
                          className="a__underline"
                        >
                          {i18n.t('page.shared.l_00001')}
                        </a>
                      </span>
                    </span>
                  </span>
                </span>
              </div>
            </div>
          )}
        </HostContainer>
        <HostDetail>
          <HostName itemProp="legalName">
            <HostLink href={ownerPath} linkColor={Colors.sublink}>
              {name}
            </HostLink>
          </HostName>
          <HostEvaluate className="clearfix">
            {evaluate(
              i18n.t('page.room.evalute1'),
              confirmRate,
              i18n.t('page.room.captionOwnerEvaluation'),
            )}
            {evaluate(i18n.t('page.room.evalute2'), replyRate, null)}
            {evaluate(i18n.t('page.room.evalute3'), replyTimeAvg, null)}
          </HostEvaluate>
        </HostDetail>
      </Inner>
      <RowLine>
        <div className="col-xs-12">
          <ProfileText>{profile}</ProfileText>
          <ProfileText>
            <a href={ownerPath}>{i18n.t('page.shared.l_00001')}</a>
          </ProfileText>
        </div>
        <div className="col-xs-12">
          <AuthContainer>
            <div className="col-xs-3 col-sm-2 ta-c">
              <AuthIcon
                src={imagePath(`${service}/common/confirm.svg`)}
                alt={i18n.t('page.room.certMark')}
              />
            </div>
            <div className="col-xs-9 col-sm-10">
              <AuthHeadLine>{i18n.t('page.room.certificated')}</AuthHeadLine>
              <AuthCheckList service={service}>
                {isVerifiedFacebook && (
                  <li>
                    <i className="fa fa-check" />
                    {i18n.t('page.room.certFacebook')}
                  </li>
                )}
                {isVerifiedEmail && (
                  <li>
                    <i className="fa fa-check" />
                    {i18n.t('page.room.certEmail')}
                  </li>
                )}
                {isVerifiedTel && (
                  <li>
                    <i className="fa fa-check" />
                    {i18n.t('page.room.certTel')}
                  </li>
                )}
              </AuthCheckList>
            </div>
          </AuthContainer>
        </div>
      </RowLine>
    </HostSummary>
  )
}



// WEBPACK FOOTER //
// ./src/components/shared/HostSummary/HostSummary.jsx